import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, LinearProgress, Snackbar, Stack, Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import editFill from '@iconify/icons-eva/edit-fill';
import pauseFill from '@iconify/icons-eva/pause-circle-fill';
import playFill from '@iconify/icons-eva/play-circle-fill';
import archiveFill from '@iconify/icons-eva/archive-fill';
import arrowBackFill from '@iconify/icons-eva/arrow-back-fill';
import eyeFill from '@iconify/icons-eva/eye-fill';
import checkSquareFill from '@iconify/icons-eva/checkmark-square-2-fill';
import Page from '../Page';
import StatusChip from './shared/StatusChip';
import MyOutreachHome from '../MyOutreach/MyOutreachHome';
import {
  getCampaign, pauseCampaign, resumeCampaign, archiveCampaign,
  stopCampaignEnrichment, getCampaignQueueStats,
} from '../../utils/outreachApi';
import stopCircleFill from '@iconify/icons-eva/stop-circle-fill';

const pct = (n, d) => (d ? `${Math.round((n / d) * 100)}%` : '—');

const CampaignDetail = () => {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const [c, setC] = useState(null);
  const [snack, setSnack] = useState(null);
  const [queueStats, setQueueStats] = useState(null);

  const load = () => getCampaign(id).then(({ data }) => {
    setC(data);
    getCampaignQueueStats(data.id).then((r) => setQueueStats(r.data)).catch(() => {});
  });

  useEffect(() => { load(); }, [id]);

  // Auto-refresh while enrichment pipeline is running
  useEffect(() => {
    if (!queueStats) return;
    const inFlight = (queueStats.by_status?.PENDING ?? 0)
      + (queueStats.by_status?.ENRICHING ?? 0)
      + (queueStats.by_status?.SCORING ?? 0)
      + (queueStats.by_status?.GENERATING ?? 0);
    if (inFlight === 0) return;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [queueStats]);

  const onAction = async (action) => {
    try {
      if (action === 'pause')   { await pauseCampaign(id);   setSnack({ severity: 'info',    message: 'Campaign paused.' }); }
      if (action === 'resume')  { await resumeCampaign(id);  setSnack({ severity: 'success', message: 'Campaign resumed.' }); }
      if (action === 'archive') { await archiveCampaign(id); setSnack({ severity: 'info',    message: 'Campaign archived.' }); }
      if (action === 'stop-enrichment') {
        const r = await stopCampaignEnrichment(id);
        setSnack({ severity: 'warning', message: `Enrichment stopped — ${r.data?.stopped ?? 0} in-flight entries cancelled.` });
      }
      load();
    } catch (err) {
      setSnack({ severity: 'error', message: err?.response?.data?.detail || 'Action failed.' });
    }
  };

  if (!c) {
    return (
      <Page title="Campaign | CRM">
        <Box sx={{ p: 4 }}><LinearProgress /></Box>
      </Page>
    );
  }

  const counts = c.counts || {};

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 24px)', bgcolor: 'background.default' }}>

      {/* ── Slim campaign header ── */}
      <Box sx={{
        px: 2.5, py: 1.25, flexShrink: 0,
        borderBottom: 1, borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
      }}>
        {/* Back */}
        <Button size="small" startIcon={<Icon icon={arrowBackFill} />}
                onClick={() => navigate('/outreach/campaigns')} sx={{ flexShrink: 0 }}>
          Campaigns
        </Button>

        {/* Name + status */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>{c.name}</Typography>
            <StatusChip status={c.display_status || c.status} />
          </Stack>
          {c.description && (
            <Typography variant="caption" color="text.secondary" noWrap>{c.description}</Typography>
          )}
        </Box>

        {/* Stats strip */}
        <Stack direction="row" spacing={2.5} sx={{ flexShrink: 0, mr: 1 }}>
          {[
            { label: 'Enrolled', value: (counts.enrolled_count ?? 0).toLocaleString() },
            { label: 'Sent',     value: (counts.sent_count     ?? 0).toLocaleString() },
            { label: 'Replied',  value: (counts.replied_count  ?? 0).toLocaleString(), color: 'warning.dark' },
            { label: 'Open %',   value: pct(counts.opened_count, counts.delivered_count) },
          ].map(({ label, value, color }) => (
            <Box key={label} sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                {label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: color || 'text.primary' }}>
                {value}
              </Typography>
            </Box>
          ))}
        </Stack>

        {/* Actions */}
        <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
          {/* Review queue badge — shown when pipeline has entries awaiting review */}
          {queueStats?.review_ready > 0 && (() => {
            const reviewContacts  = queueStats.review_ready;
            const reviewCompanies = queueStats.distinct_companies_by_status?.REVIEW;
            const label = reviewCompanies && reviewCompanies !== reviewContacts
              ? `Review queue (${reviewCompanies} cos · ${reviewContacts} contacts)`
              : `Review queue (${reviewContacts})`;
            return (
              <Button
                size="small"
                startIcon={<Icon icon={checkSquareFill} />}
                variant="contained"
                color="warning"
                onClick={() => navigate(`/my-outreach/review?campaign_id=${id}&all_pms=1`)}
              >
                {label}
              </Button>
            );
          })()}
          {/* Stop enrichment — only when pipeline has in-flight entries */}
          {(() => {
            const inFlight = (queueStats?.by_status?.PENDING ?? 0)
              + (queueStats?.by_status?.ENRICHING ?? 0)
              + (queueStats?.by_status?.SCORING ?? 0)
              + (queueStats?.by_status?.GENERATING ?? 0);
            if (inFlight === 0) return null;
            return (
              <Button
                size="small"
                startIcon={<Icon icon={stopCircleFill} />}
                variant="outlined"
                color="error"
                onClick={() => {
                  // eslint-disable-next-line no-restricted-globals, no-alert
                  if (confirm(`Stop enrichment for ${inFlight} in-flight entries? Already-completed entries won't be affected.`)) {
                    onAction('stop-enrichment');
                  }
                }}
              >
                Stop ({inFlight})
              </Button>
            );
          })()}
          <Button size="small" startIcon={<Icon icon={editFill} />} variant="outlined"
                  onClick={() => navigate(`/outreach/campaigns/${id}/edit`)}>Edit</Button>
          {c.status === 'active' && (
            <Button size="small" startIcon={<Icon icon={pauseFill} />} variant="outlined" color="warning"
                    onClick={() => onAction('pause')}>Pause</Button>
          )}
          {c.status === 'paused' && (
            <Button size="small" startIcon={<Icon icon={playFill} />} variant="outlined" color="success"
                    onClick={() => onAction('resume')}>Resume</Button>
          )}
          {c.status !== 'archived' && (
            <Button size="small" startIcon={<Icon icon={archiveFill} />} variant="outlined"
                    onClick={() => onAction('archive')}>Archive</Button>
          )}
        </Stack>
      </Box>

      {/* Pipeline running banner — live validation progress while enrichment runs.
          Companies are validated one at a time (staggered to respect API rate
          limits), so this can take a while for large campaigns. Each company
          becomes reviewable the moment its own pipeline finishes — the admin
          doesn't have to wait for the whole batch. */}
      {c.status === 'draft' && queueStats && (() => {
        const bs = queueStats.by_status || {};
        const inFlight = (bs.PENDING ?? 0) + (bs.ENRICHING ?? 0) + (bs.SCORING ?? 0) + (bs.GENERATING ?? 0);
        if (inFlight === 0) return null;
        const total   = queueStats.total ?? inFlight;
        const done    = Math.max(0, total - inFlight);
        const percent = total ? Math.round((done / total) * 100) : 0;
        const reviewReady = queueStats.review_ready ?? 0;
        return (
          <Box sx={{
            px: 2.5, py: 1, flexShrink: 0,
            bgcolor: 'info.lighter',
            borderBottom: 1, borderColor: 'info.light',
          }}>
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 0.5 }}>
              <CircularProgress size={14} thickness={5} />
              <Typography variant="caption" color="info.dark" fontWeight={700}>
                Validating companies — {done.toLocaleString()} of {total.toLocaleString()} done ({percent}%)
                {queueStats.distinct_companies ? ` · ${queueStats.distinct_companies} companies` : ''}.
              </Typography>
              <Box sx={{ flex: 1 }} />
              {reviewReady > 0 && (
                <Typography variant="caption" color="warning.dark" fontWeight={700}>
                  {reviewReady} ready to review now
                </Typography>
              )}
            </Stack>
            <LinearProgress variant="determinate" value={percent} sx={{ height: 6, borderRadius: 3 }} />
            <Typography variant="caption" color="info.dark" sx={{ display: 'block', mt: 0.5 }}>
              Each company becomes reviewable as soon as it finishes — you can start reviewing the ready ones now. Auto-refreshes every 10s.
            </Typography>
          </Box>
        );
      })()}

      {/* Draft with no pipeline activity — explain next step */}
      {c.status === 'draft' && queueStats && queueStats.total === 0 && (
        <Box sx={{
          px: 2.5, py: 1, flexShrink: 0,
          bgcolor: 'background.neutral',
          borderBottom: 1, borderColor: 'divider',
        }}>
          <Typography variant="caption" color="text.secondary">
            Pipeline starting — matching companies from your rules will appear in the Review queue shortly.
            {!c.pm_assignments?.length && ' Add BA assignments to enable activation.'}
          </Typography>
        </Box>
      )}

      {/* Draft with review-ready entries — prompt to review */}
      {c.status === 'draft' && queueStats?.review_ready > 0 && (
        <Box sx={{
          px: 2.5, py: 1, flexShrink: 0,
          bgcolor: 'warning.lighter',
          borderBottom: 1, borderColor: 'warning.light',
        }}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Typography variant="caption" color="warning.dark" fontWeight={700}>
              {queueStats.review_ready} contacts
              {queueStats.distinct_companies_by_status?.REVIEW
                && queueStats.distinct_companies_by_status.REVIEW !== queueStats.review_ready
                  ? ` across ${queueStats.distinct_companies_by_status.REVIEW} companies`
                  : ''}
              {' '}ready for review. Approve them, then click Activate to start sending.
            </Typography>
          </Stack>
        </Box>
      )}

      {/* Demo mode banner */}
      {c.is_demo && (
        <Box sx={{
          px: 2.5, py: 1, flexShrink: 0,
          bgcolor: 'warning.lighter',
          borderBottom: 1, borderColor: 'warning.light',
        }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Icon icon={eyeFill} width={16} color="#B76E00" />
            <Typography variant="caption" color="warning.dark" fontWeight={700}>
              Demo mode — enrichment + AI scoring runs as normal, but all sends are skipped.
              Check the review queue to see what messages were drafted for each company.
            </Typography>
          </Stack>
        </Box>
      )}

      {/* ── 3-panel view filling the rest ── */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {/* No initialPmId: admins view ALL BAs' conversations by default.
            (pm_assignments is the legacy pre-decouple field and is empty, which
            previously made this fall back to the admin and show an empty list.) */}
        <MyOutreachHome campaign={c} />
      </Box>

      <Snackbar open={!!snack} autoHideDuration={3500} onClose={() => setSnack(null)}>
        {snack && <Alert severity={snack.severity}>{snack.message}</Alert>}
      </Snackbar>
    </Box>
  );
};

export default CampaignDetail;
